// ============================================
// FILE: src/components/fashion/FashionProductGallery.jsx
// ============================================
//
// Two distinct presentations, matching how fashion marketplaces actually
// behave:
//
//   Desktop — a two-column grid of every image, all visible at once. Shoppers
//             scroll the page rather than click through a carousel.
//   Mobile  — a swipeable full-bleed carousel with dot indicators.
//
// Videos sit inline in both. They never autoplay, and a video that scrolls
// out of view is paused so its audio does not continue over the next image.

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Play, X, Expand, ChevronUp, ChevronDown } from 'lucide-react';
import MediaLightbox from './MediaLightbox';
import {
  isVideo,
  getMediaFull,
  getMediaSrcSet,
  getMediaThumb,
  getMediaPoster,
  getEmbedUrl,
  filterMediaByValue,
} from '../../utils/mediaHelpers';
import { BLANK_PRODUCT_IMAGE } from '../../utils/imageHelpers';

const SWIPE_THRESHOLD = 45;

// Desktop thumbnail rail. Portrait to match the stage, so a thumb previews the
// framing of the image it opens rather than a square crop of its middle.
const THUMB_W = 62;
const THUMB_H = 82;
const THUMB_GAP = 8;

const FashionProductGallery = ({
  media = [],
  activeValueId = null,
  productName = '',
  isDesktop = false,
}) => {
  const [index, setIndex] = useState(0);
  const [playingId, setPlayingId] = useState(null);
  const [lightboxAt, setLightboxAt] = useState(null);   // index, or null when closed
  const touchStartX = useRef(null);
  const videoRefs = useRef({});

  // Rail scroll affordances — the chevrons only earn their space when there
  // are more thumbs than fit, and each disables at its end of the track.
  const railRef = useRef(null);
  const [railOverflows, setRailOverflows] = useState(false);
  const [atRailTop, setAtRailTop] = useState(true);
  const [atRailBottom, setAtRailBottom] = useState(false);

  const updateRailEdges = useCallback(() => {
    const el = railRef.current;
    if (!el) return;

    const overflowing = el.scrollHeight > el.clientHeight + 1;
    setRailOverflows(overflowing);
    setAtRailTop(el.scrollTop <= 1);
    setAtRailBottom(el.scrollTop + el.clientHeight >= el.scrollHeight - 1);
  }, []);

  const scoped = useMemo(
    () => filterMediaByValue(media, activeValueId),
    [media, activeValueId]
  );

  useEffect(() => {
    setIndex(0);
    setPlayingId(null);
  }, [activeValueId]);

  useEffect(() => {
    if (index > scoped.length - 1) setIndex(0);
  }, [scoped.length, index]);

  // Re-measure the rail when the image set changes (a colour switch can take
  // it from overflowing to not) and when the window resizes.
  useEffect(() => {
    if (!isDesktop) return undefined;

    updateRailEdges();
    window.addEventListener('resize', updateRailEdges);
    return () => window.removeEventListener('resize', updateRailEdges);
  }, [isDesktop, scoped.length, updateRailEdges]);

  // Keep the selected thumb visible — arrow-keying or a colour change can
  // otherwise leave the highlighted thumb scrolled out of the rail.
  useEffect(() => {
    if (!isDesktop) return;

    const rail = railRef.current;
    const thumb = rail?.querySelector(`[data-thumb="${index}"]`);
    thumb?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [index, isDesktop]);

  // Pause any video whose slide is no longer on screen.
  useEffect(() => {
    if (isDesktop) return;

    Object.entries(videoRefs.current).forEach(([id, el]) => {
      if (el && String(scoped[index]?.id) !== String(id)) el.pause();
    });

    if (playingId != null && String(scoped[index]?.id) !== String(playingId)) {
      setPlayingId(null);
    }
  }, [index, scoped, playingId, isDesktop]);

  if (scoped.length === 0) {
    return (
      <div
        className="relative w-full bg-[#f5f5f6] overflow-hidden"
        style={{ aspectRatio: '3 / 4' }}
      >
        <img
          src={BLANK_PRODUCT_IMAGE}
          alt={productName}
          className="absolute inset-0 w-full h-full object-cover"
        />
      </div>
    );
  }

  const renderMedia = (item, slideIndex, { contain = false } = {}) => {
    if (!isVideo(item)) {
      // Images open the full-screen viewer; videos keep their own play button.
      return (
        <button
          type="button"
          onClick={() => setLightboxAt(slideIndex)}
          className="group relative w-full h-full block"
          aria-label={`View ${item.alt_text || productName} full screen`}
        >
          <img
            src={getMediaFull(item)}
            srcSet={getMediaSrcSet(item)}
            // Desktop stage is the 600px column minus the thumb rail and gap,
            // so ~520px; mobile is full-bleed. Without this the browser assumes
            // 100vw and over-downloads on every device.
            sizes={isDesktop ? '(min-width: 1024px) 520px, 100vw' : '100vw'}
            alt={item.alt_text || `${productName} view ${slideIndex + 1}`}
            className={`w-full h-full ${contain ? 'object-contain' : 'object-cover'}`}
            loading={slideIndex === 0 ? 'eager' : 'lazy'}
            decoding={slideIndex === 0 ? 'sync' : 'async'}
            fetchpriority={slideIndex === 0 ? 'high' : undefined}
          />
          <span className="absolute bottom-2 right-2 w-8 h-8 rounded-full bg-white/85 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <Expand size={15} className="text-[#282c3f]" />
          </span>
        </button>
      );
    }

    const isPlaying = String(playingId) === String(item.id);
    const poster = getMediaPoster(item);

    if (!isPlaying) {
      return (
        <button
          type="button"
          onClick={() => setPlayingId(item.id)}
          className="relative w-full h-full block bg-black"
          aria-label={`Play ${item.alt_text || productName} video`}
        >
          {poster && (
            <img
              src={poster}
              alt={item.alt_text || `${productName} video`}
              className="w-full h-full object-cover opacity-90"
              loading="lazy"
            />
          )}
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="w-14 h-14 rounded-full bg-white/95 shadow-lg flex items-center justify-center">
              <Play size={24} className="text-[#282c3f] ml-1" fill="currentColor" />
            </span>
          </span>
          {item.duration_seconds > 0 && (
            <span
              className="absolute bottom-3 right-3 px-1.5 py-0.5 bg-black/70 text-white"
              style={{ fontSize: 11, borderRadius: 2 }}
            >
              {Math.floor(item.duration_seconds / 60)}:
              {String(item.duration_seconds % 60).padStart(2, '0')}
            </span>
          )}
        </button>
      );
    }

    return (
      <div className="relative w-full h-full bg-black">
        {item.provider === 'upload' ? (
          <video
            ref={(el) => { videoRefs.current[item.id] = el; }}
            src={getMediaFull(item)}
            poster={poster || undefined}
            controls
            playsInline
            autoPlay
            preload="metadata"
            className="w-full h-full object-contain"
          />
        ) : (
          <iframe
            src={getEmbedUrl(item, { autoplay: true })}
            title={item.alt_text || `${productName} video`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            loading="lazy"
            className="w-full h-full border-0"
          />
        )}
        <button
          type="button"
          onClick={() => setPlayingId(null)}
          className="absolute top-2 right-2 p-1.5 bg-black/60 rounded-full"
          aria-label="Close video"
        >
          <X size={16} className="text-white" />
        </button>
      </div>
    );
  };

  const lightbox = (
    <MediaLightbox
      media={scoped}
      startIndex={lightboxAt ?? 0}
      isOpen={lightboxAt !== null}
      onClose={() => setLightboxAt(null)}
      productName={productName}
      isDesktop={isDesktop}
    />
  );

  // ---- Desktop: vertical thumbnail rail beside one large stage ---------- //
  if (isDesktop) {
    const scrollRail = (direction) => {
      // One thumb height per press, so a click always advances by a whole
      // image rather than leaving one sliced across the top edge.
      railRef.current?.scrollBy({ top: direction * (THUMB_H + THUMB_GAP), behavior: 'smooth' });
    };

    return (
      <>
        <div className="flex gap-4 items-stretch">
          {/* Thumbnail rail */}
          <div className="flex flex-col items-center flex-shrink-0" style={{ width: THUMB_W }}>
            <button
              type="button"
              onClick={() => scrollRail(-1)}
              disabled={!railOverflows || atRailTop}
              className="py-1 disabled:opacity-25"
              aria-label="Scroll thumbnails up"
              style={{ visibility: railOverflows ? 'visible' : 'hidden' }}
            >
              <ChevronUp size={18} style={{ color: 'var(--fx-ink-2)' }} />
            </button>

            <div
              ref={railRef}
              onScroll={updateRailEdges}
              className="flex-1 overflow-y-auto hide-scrollbar w-full"
            >
              <div className="flex flex-col items-center" style={{ gap: THUMB_GAP }}>
                {scoped.map((item, i) => {
                  const active = i === index;

                  return (
                    <button
                      key={item.id ?? i}
                      type="button"
                      data-thumb={i}
                      onClick={() => setIndex(i)}
                      className="relative flex-shrink-0 bg-[#f5f5f6] overflow-hidden"
                      style={{
                        width: THUMB_W,
                        height: THUMB_H,
                        // The active thumb gets a boxed outline, offset so it
                        // frames the image rather than cropping into it.
                        outline: active ? '1px solid var(--fx-ink)' : '1px solid transparent',
                        outlineOffset: 2,
                      }}
                      aria-label={`Show image ${i + 1}`}
                      aria-current={active}
                    >
                      <img
                        src={getMediaThumb(item)}
                        alt=""
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      {isVideo(item) && (
                        <span className="absolute inset-0 flex items-center justify-center bg-black/25">
                          <Play size={14} className="text-white" fill="currentColor" />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <button
              type="button"
              onClick={() => scrollRail(1)}
              disabled={!railOverflows || atRailBottom}
              className="py-1 disabled:opacity-25"
              aria-label="Scroll thumbnails down"
              style={{ visibility: railOverflows ? 'visible' : 'hidden' }}
            >
              <ChevronDown size={18} style={{ color: 'var(--fx-ink-2)' }} />
            </button>
          </div>

          {/* Main stage */}
          <div
            className="relative flex-1 bg-[#f5f5f6] overflow-hidden"
            style={{ aspectRatio: '3 / 4' }}
          >
            {renderMedia(scoped[index], index, { contain: isVideo(scoped[index]) })}
          </div>
        </div>
        {lightbox}
      </>
    );
  }

  // ---- Mobile: swipeable carousel --------------------------------------- //
  return (
    <div className="relative">
      <div
        className="relative w-full overflow-hidden bg-[#f5f5f6]"
        style={{ aspectRatio: '3 / 4', touchAction: 'pan-y' }}
        onTouchStart={(e) => { touchStartX.current = e.touches?.[0]?.clientX ?? null; }}
        onTouchEnd={(e) => {
          if (touchStartX.current == null) return;
          const dx = (e.changedTouches?.[0]?.clientX ?? touchStartX.current) - touchStartX.current;
          touchStartX.current = null;
          if (Math.abs(dx) < SWIPE_THRESHOLD) return;
          const next = dx < 0 ? index + 1 : index - 1;
          if (next >= 0 && next <= scoped.length - 1) setIndex(next);
        }}
      >
        <div
          className="flex h-full"
          style={{
            transform: `translateX(-${index * 100}%)`,
            transition: 'transform 280ms ease',
          }}
        >
          {scoped.map((item, i) => (
            <div key={item.id ?? i} className="w-full h-full flex-shrink-0">
              {renderMedia(item, i)}
            </div>
          ))}
        </div>

        {scoped.length > 1 && (
          <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
            {scoped.map((item, i) => (
              <span
                key={item.id ?? i}
                style={{
                  width: i === index ? 16 : 5,
                  height: 5,
                  borderRadius: 9999,
                  background: i === index ? 'var(--fx-pink)' : 'rgba(0,0,0,0.2)',
                  transition: 'width 160ms ease',
                }}
              />
            ))}
          </div>
        )}
      </div>
      {lightbox}
    </div>
  );
};

export default FashionProductGallery;
